import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}

interface WeekendCampaignBannerProps {
  isBannerVisible: boolean;
}

export function WeekendCampaignBanner({ isBannerVisible }: WeekendCampaignBannerProps) {
  const [isWeekend, setIsWeekend] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ hours: 0, minutes: 0, seconds: 0 });

  // Check if current time is weekend and calculate countdown
  useEffect(() => {
    const updateWeekendStatus = () => {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const currentIsWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Saturday or Sunday
      setIsWeekend(currentIsWeekend);
      if (currentIsWeekend) {
        // Calculate time until Monday 12:00 AM
        const nextMonday = new Date(now);
        if (dayOfWeek === 6) {
          nextMonday.setDate(now.getDate() + 2);
        } else {
          nextMonday.setDate(now.getDate() + 1);
        }
        nextMonday.setHours(0, 0, 0, 0);
        const timeDiff = nextMonday.getTime() - now.getTime();
        if (timeDiff > 0) {
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));
          const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
          setTimeLeft({ hours, minutes, seconds });
        } else {
          setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        }
      }
    };
    updateWeekendStatus();
    const interval = setInterval(updateWeekendStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (time: number) => time.toString().padStart(2, '0');

  return (
    <div
      className={`fixed top-24 px-0.5 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-7xl transition-opacity duration-1000 ${isBannerVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      style={{ transitionDuration: '600ms' }}
    >
      <div
        className={`
          relative overflow-hidden border backdrop-blur-md
          ${isWeekend
            ? 'bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark animate-gradient bg-[length:200%_auto] border-white/20 dark:border-white/10'
            : 'bg-primary-light dark:bg-primary-dark border-primary-light/20 dark:border-primary-dark/20'}
          shadow-lg
          rounded-b-2xl rounded-t-none
          transition-all duration-700
        `}
        style={{ transformOrigin: 'center' }}
      >
        {/* Shine effect for weekend state */}
        {isWeekend && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shine" />
        )}
        <div className="relative flex flex-row items-center justify-center text-center px-2 md:px-4 py-2.5 pt-5 w-full">
          <div
            className={`flex flex-row  items-center gap-2 w-full justify-center transition-opacity duration-500 opacity-100`}
          >
            <span className="text-xs md:text-sm font-bold text-white hidden md:block">#FreeMockWeekends -</span>
            {isWeekend && (
              <span className="text-xs md:text-sm font-bold text-white -ml-1 hidden md:block ">
                It's Weekend,
              </span>
            )}
            <span className="text-sm md:text-sm font-medium text-white -ml-1">
              MockTrail is <span className="font-heading font-bold">100% FREE</span> To Practice on Every Saturday & Sunday. <span
              className="md:hidden font-heading ml-1 -mt-0.5 text-sm font-semibold text-white underline decoration-2 underline-offset-2 cursor-pointer select-none hover:scale-105 transition-all duration-300"
              onClick={() => {
                const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
                window.open(`${platformUrl}/login?utm_source=weekend_campaign&utm_medium=banner&utm_campaign=free_weekend`, '_blank');
              }}
            >
              {isWeekend ? 'Start Practicing' : 'Sign up Now'}
            </span>
            </span>
            <span
              className="hidden md:block font-heading ml-1 -mt-0.5 text-xs md:text-sm font-semibold text-white underline decoration-2 underline-offset-2 cursor-pointer select-none hover:scale-105 transition-all duration-300"
              onClick={() => {
                const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
                window.open(`${platformUrl}/login?utm_source=weekend_campaign&utm_medium=banner&utm_campaign=free_weekend`, '_blank');
              }}
            >
              {isWeekend ? 'Start Practicing' : 'Sign up Now'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}